import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import {
  setAbsenceManagementSelectedLocations,
  setFullTimeScheduleSelectedLocations,
  updateCompanyAbsenceManagementLocationSettings,
  updateCompanyFullTimeScheduleLocationSettings,
  updateCompanySettings
} from "../../../../../../../export/gat-admin";
import useGatAdminConfigData from "../../../../../../../ts/components/gat-admin/controllers/use-gat-admin-config-data";
import { findDifferentKeys } from "../../../../utils";

const addOnObj: any = {
  flexSchedulingEnabled: "clientApp.flexSchedulingEnabled",
  crossSkillingEnabled: "clientApp.crossSkillingEnabled",
  skillInsightsEnabled: "clientApp.skillInsightsEnabled",
  skillsMatrixExportEnabled: "skillMatrix.excelExport.enabled",
  jobsMatrixEnabled: "jobsMatrix.enabled",
  skillEvaluationFormEnabled: "skillEvaluationForm.enabled",
  skillPriorityEnabled: "skillPriority.Enabled",
  skillPriorityWorkerAppEnabled: "skillPriority.showOnWorkerApp",
  skillPriorityObsoleteSkillEnabled:
    "skillPriority.workerScore.useObsoleteSkill",
  skillLevelRequirementsEnabled: "skillLevelRequirements.enabled",
  certificationsEnabled: "clientApp.certificationsEnabled",
  skillExpiryEnabled: "clientApp.skillExpiryEnabled",
  workerProfileEnabled: "clientApp.workerProfileEnabled",
  shiftBySkillsEnabled: "shiftBySkills.enabled",
  shiftBySkillsMinSkillLevel: "shiftBySkills.minSkillLevel",
  skillLevelJobMappingEnabled: "jobSkill.skillLevel.enabled",
  workerSubTypeEnabled: "workerSubType.enabled",
  fullTimeSchedulingEnabled: "fullTimeScheduling.enabled",
  absenceManagementEnabled: "attendMgmt.enabled",
  leaveBalanceUpdateEnabled: "leaveBalanceMgmt.enabled",
  supervisorMultilingualEnabled: "supervisorMultilingual.enabled",
  revenueForecastingEnabled: "clientApp.revenueForecastingEnabled",
  labBoardEnabled: "clientApp.labBoardEnabled",
  supervisorDashboardEnabled: "supervisorDashboard.enabled"
};

const initialAddOnFeatures = {
  flexSchedulingEnabled: false,
  crossSkillingEnabled: false,
  skillInsightsEnabled: false,
  skillsMatrixExportEnabled: false,
  jobsMatrixEnabled: false,
  skillEvaluationFormEnabled: false,
  skillPriorityEnabled: false,
  skillPriorityWorkerAppEnabled: false,
  skillPriorityObsoleteSkillEnabled: false,
  skillLevelRequirementsEnabled: false,
  certificationsEnabled: false,
  skillExpiryEnabled: false,
  shiftBySkillsEnabled: false,
  shiftBySkillsMinSkillLevel: 0,
  workerProfileEnabled: false,
  skillLevelJobMappingEnabled: false,
  workerSubTypeEnabled: false,
  fullTimeSchedulingEnabled: false,
  absenceManagementEnabled: false,
  leaveBalanceUpdateEnabled: false,
  supervisorMultilingualEnabled: false,
  revenueForecastingEnabled: false,
  labBoardEnabled: false,
};

const useAddOnController = () => {
  const dispatch = useDispatch();
  const {
    updateCompanySettingsLevelStatus,
    companySettingsObj,
    companyDataValue,
    updateCompanySettingsStatus,
    fullTimeSchedulingLocationSettings,
    fullTimeScheduleSelectedLocations,
    absenceManagementLocationSettings,
    absenceManagementSelectedLocations,
    getCompanySettingsStatus,
  } = useGatAdminConfigData();

  const [addOnSettings, setAddOnSettings] = useState(initialAddOnFeatures);
  const [newAddOnSettings, setNewAddOnSettings] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const handleSave = () => {
    setLoading(true);
    const diffKeys: any = findDifferentKeys(addOnSettings, newAddOnSettings);
    const changedSettings: any = diffKeys.map((key: any) => {
      const code = addOnObj[key];
      const existing = companySettingsObj[code];
      if (existing) {
        return {
          ...existing,
          value: newAddOnSettings[key],
        };
      }
      // Setting not yet present on the company — construct it.
      return {
        code,
        entity: "company",
        entityId: companyDataValue?.id,
        type: "System.Boolean",
        value: newAddOnSettings[key],
      };
    });
    if (newAddOnSettings.fullTimeSchedulingEnabled) {
      const newLocationSettings = fullTimeSchedulingLocationSettings.map(
        (location: any) => {
          const selectedLocation = fullTimeScheduleSelectedLocations.find(
            (loc: any) => loc.id === location.locationId
          );
          return {
            ...location,
            value: !!selectedLocation,
          };
        }
      );
      if (JSON.stringify(fullTimeSchedulingLocationSettings) !== JSON.stringify(newLocationSettings)) {
        // Dispatch the action to update the company full-time schedule location settings
        dispatch(
          updateCompanyFullTimeScheduleLocationSettings({
            companyId: companyDataValue?.id,
            locationSettings: newLocationSettings,
            originalLocationSettings: fullTimeSchedulingLocationSettings,
          })
        );
      }
    }

    if (newAddOnSettings.absenceManagementEnabled) {
      const newLocationSettings = absenceManagementLocationSettings.map(
        (location: any) => {
          const selectedLocation = absenceManagementSelectedLocations.find(
            (loc: any) => loc.id === location.locationId
          );
          return {
            ...location,
            value: !!selectedLocation,
          };
        }
      );
      if (JSON.stringify(absenceManagementLocationSettings) !== JSON.stringify(newLocationSettings)) {
        // Dispatch the action to update the company absence management location settings
        dispatch(
          updateCompanyAbsenceManagementLocationSettings({
            companyId: companyDataValue?.id,
            locationSettings: newLocationSettings,
            originalLocationSettings: absenceManagementLocationSettings,
          })
        );
      }
    }
    if (changedSettings) {
      if (changedSettings.length > 0) {
        // Check if the fullTimeSchedulingEnabled is true
        const fullTimeScheduling = changedSettings.find(
          (changedSetting: { code: string }) =>
            changedSetting.code === "fullTimeScheduling.enabled"
        );
        if (fullTimeScheduling) {
          if (!fullTimeScheduling.value) {
            const newLocationSettings = fullTimeSchedulingLocationSettings.map(
              (location: any) => {
                return {
                  ...location,
                  value: false,
                };
              }
            );
            dispatch(
              updateCompanyFullTimeScheduleLocationSettings({
                companyId: companyDataValue?.id,
                locationSettings: newLocationSettings,
                originalLocationSettings: fullTimeSchedulingLocationSettings,
              })
            );
          }
        }

        // Check if the absenceMgmt is true
        const absenceManagement = changedSettings.find(
            (changedSetting: { code: string }) =>
                changedSetting.code === "attendMgmt.enabled"
        );

        if (absenceManagement) {
          if (!absenceManagement.value) {
            const newLocationSettings = absenceManagementLocationSettings.map(
                (location: any) => {
                  return {
                    ...location,
                    value: false,
                  };
                }
            );
            dispatch(
                updateCompanyAbsenceManagementLocationSettings({
                  companyId: companyDataValue?.id,
                  locationSettings: newLocationSettings,
                  originalLocationSettings: absenceManagementLocationSettings,
                })
            );
          }
        }

        // Dispatch the action to update the company settings
        dispatch(updateCompanySettings(changedSettings));
      }
    }
  };

  useEffect(() => {
    if (Object.keys(companySettingsObj).length > 0) {
      const obj = {
        flexSchedulingEnabled: false,
        crossSkillingEnabled:
          companySettingsObj["clientApp.crossSkillingEnabled"]?.value,
        skillInsightsEnabled:
          companySettingsObj["clientApp.skillInsightsEnabled"]?.value,
        skillsMatrixExportEnabled:
          companySettingsObj["skillMatrix.excelExport.enabled"]?.value,
        jobsMatrixEnabled:
          companySettingsObj["jobsMatrix.enabled"]?.value,
        skillEvaluationFormEnabled:
          companySettingsObj["skillEvaluationForm.enabled"]?.value,
        skillPriorityEnabled:
          companySettingsObj["skillPriority.Enabled"]?.value,
        skillPriorityWorkerAppEnabled:
          companySettingsObj["skillPriority.showOnWorkerApp"]?.value,
        skillPriorityObsoleteSkillEnabled:
          companySettingsObj["skillPriority.workerScore.useObsoleteSkill"]
            ?.value,
        shiftBySkillsEnabled: companySettingsObj["shiftBySkills.enabled"]?.value,
        shiftBySkillsMinSkillLevel:
          companySettingsObj["shiftBySkills.minSkillLevel"]?.value,
        certificationsEnabled:
          companySettingsObj["clientApp.certificationsEnabled"]?.value,
        skillExpiryEnabled:
          companySettingsObj["clientApp.skillExpiryEnabled"]?.value,
        workerProfileEnabled:
          companySettingsObj["clientApp.workerProfileEnabled"]?.value,
        skillLevelJobMappingEnabled:
          companySettingsObj["jobSkill.skillLevel.enabled"]?.value,
        skillLevelRequirementsEnabled:
          companySettingsObj["skillLevelRequirements.enabled"]?.value,
        workerSubTypeEnabled: companySettingsObj["workerSubType.enabled"]?.value,
        fullTimeSchedulingEnabled:
          companySettingsObj["fullTimeScheduling.enabled"]?.value,
        absenceManagementEnabled:
            companySettingsObj["attendMgmt.enabled"]?.value,
        leaveBalanceUpdateEnabled:
            companySettingsObj["leaveBalanceMgmt.enabled"]?.value,
        supervisorMultilingualEnabled:
            companySettingsObj["supervisorMultilingual.enabled"]?.value ?? false,
        revenueForecastingEnabled:
          companySettingsObj["clientApp.revenueForecastingEnabled"]?.value ??
          false,
        labBoardEnabled:
          companySettingsObj["clientApp.labBoardEnabled"]?.value ?? false,
        supervisorDashboardEnabled:
            companySettingsObj["supervisorDashboard.enabled"]?.value ?? false,
      };
      setAddOnSettings(obj);
      setNewAddOnSettings(obj);
    }
  }, [companySettingsObj]);

  useEffect(() => {
    if (updateCompanySettingsStatus === "fulfilled") {
      setLoading(false);
    }
  }, [updateCompanySettingsStatus]);

  useEffect(() => {
    if (updateCompanySettingsLevelStatus === "fulfilled") {
      setLoading(false);
    }
  }, [updateCompanySettingsLevelStatus]);

  const resetCompanyAbsenceManagementLocations = () => {
    const originalSelectedLocations = absenceManagementLocationSettings.filter(
      (location: any) => location.value
    ).map((location: any) => ({
      id: location.locationId,
      name: location.locationName,
    })
    );
    dispatch(setAbsenceManagementSelectedLocations(originalSelectedLocations));
  };

  const resetCompanyFullTimeScheduleLocations = () => {
    const originalSelectedLocations = fullTimeSchedulingLocationSettings.filter(
      (location: any) => location.value
    ).map((location: any) => ({
      id: location.locationId,
      name: location.locationName,
    })
    );
    dispatch(setFullTimeScheduleSelectedLocations(originalSelectedLocations));
  }

  return [
    {
      addOnSettings,
      newAddOnSettings,
      companyDataValue,
      updateCompanySettingsLevelStatus,
      updateCompanySettingsStatus,
      getCompanySettingsStatus,
      loading,
    },
    {
      setNewAddOnSettings,
      handleSave,
      resetCompanyFullTimeScheduleLocations,
      resetCompanyAbsenceManagementLocations,
    },
  ];
};

export default useAddOnController;
